import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, ChevronRight, Sparkles, FileText, MessageSquareQuote, BookOpen } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${Math.round(n || 0).toLocaleString()}`;

const LOCKED_SECTIONS = [
  { icon: FileText, title: 'Step-by-step action plan', lines: ['Set reasonable salary at', 'Open Solo 401(k) before', 'Measure your workspace and'] },
  { icon: MessageSquareQuote, title: 'CPA talking points', lines: ['Ask whether an election by', 'Confirm your safe-harbor', 'Review accountable plan for'] },
  { icon: BookOpen, title: 'IRS citations for every strategy', lines: ['Publication 535 —', 'Publication 560 —', 'Publication 587 —'] },
];

const LockedSection = ({ icon: Icon, title, lines }) => (
  <div className="relative overflow-hidden rounded-xl border border-stone-200 bg-white p-5" data-testid="locked-report-section">
    <p className="flex items-center gap-2 text-sm font-semibold text-stone-900"><Icon className="h-4 w-4 text-emerald-600" /> {title}</p>
    <div className="mt-3 space-y-2 select-none blur-[5px]" aria-hidden="true">
      {lines.map(l => <p key={l} className="text-sm text-stone-600">{l} $4,820 by filing the right form this quarter.</p>)}
    </div>
    <Lock className="absolute right-4 top-4 h-4 w-4 text-stone-400" />
  </div>
);

export const LockedStrategyPreview = () => {
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    axios.get(`${API}/tax-snapshot/setup-preview`, { withCredentials: true }).then(r => setPreview(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-10 md:py-16" data-testid="strategy-advisor-upgrade">
      <div className="mx-auto max-w-5xl grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <div>
          <p className="inline-flex items-center gap-2 rounded-full bg-emerald-100 px-3 py-1 text-xs font-medium text-emerald-800"><Lock className="h-3.5 w-3.5" /> Strategy Advisor report preview</p>
          <h1 className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight text-stone-900">Your strategy report is ready to unlock</h1>
          {preview ? (
            <div className="mt-6" data-testid="locked-preview-savings">
              <p className="text-sm text-stone-500">Estimated savings we found {preview.mode === 'personalized' ? 'in your numbers' : `for a typical ${preview.business_label}`}</p>
              <p className="mt-1 font-mono text-4xl font-bold tabular-nums text-emerald-700" data-testid="locked-preview-total">{money(preview.total_low)} – {money(preview.total_high)}<span className="ml-1 text-base font-normal text-stone-500">/ yr</span></p>
            </div>
          ) : null}
          <p className="mt-5 text-stone-600 leading-relaxed" data-testid="strategy-upgrade-message">
            Strategy Advisor is included with every paid SolelyTax plan. Upgrade to get a short interview tailored to your business and a full report with exact steps, deadlines and CPA-ready talking points.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button size="lg" onClick={() => navigate('/pricing?return_to=strategy-advisor')} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="strategy-upgrade-btn">
              Unlock my full report <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
            <Button size="lg" variant="ghost" onClick={() => navigate('/dashboard')} className="rounded-full" data-testid="strategy-back-dashboard-btn">Back to dashboard</Button>
          </div>
        </div>

        <div className="space-y-4">
          {preview?.strategies?.length > 0 && (
            <div className="rounded-xl bg-stone-900 p-5 text-white" data-testid="locked-preview-strategies">
              <p className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-300"><Sparkles className="h-3.5 w-3.5" /> Strategies in your report</p>
              <ul className="mt-3 divide-y divide-white/10">
                {preview.strategies.map(s => (
                  <li key={s.id} className="flex items-center justify-between gap-4 py-2.5 text-sm" data-testid={`locked-preview-strategy-${s.id}`}>
                    <span className="text-stone-200">{s.title}{s.id === 'qbi' && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-300/80">already counted</span>}</span>
                    <span className="shrink-0 whitespace-nowrap font-mono tabular-nums text-emerald-300">{s.display}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {LOCKED_SECTIONS.map(s => <LockedSection key={s.title} {...s} />)}
          {preview && <p className="text-[11px] leading-relaxed text-stone-500">{preview.disclaimer}</p>}
        </div>
      </div>
    </div>
  );
};

export default LockedStrategyPreview;
